import { cn } from '@heroui/react'
import { Button } from 'components/ui/Button'
import { useNotifications } from 'hooks/useNotifications'
import { Bell, CheckCheck, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Dialog, DialogTrigger, Popover } from 'react-aria-components'
import { useNavigate } from 'react-router-dom'
import { toDtmy } from 'utils/formatting/toDtmy'

export function NotificationBell() {
  const navigate = useNavigate()
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading, deleteNotification, deleteAllNotifications } = useNotifications()
  const [isOpen, setIsOpen] = useState(false)

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id)
    }

    // Close the popover
    setIsOpen(false)

    // Navigate based on notification type
    const metadata = notification.metadata || {}
    if (metadata.call_id) {
      navigate(`/dashboard/call-logs/${metadata.call_id}?activeTab=review`)
    }
  }

  const getNotificationMessage = (notification) => {
    const { type } = notification

    switch (type) {
      case 'mention':
        return `mentioned you in a note`
      case 'assignment':
        return `assigned a call to you`
      default:
        return 'sent you a notification'
    }
  }

  const getNotificationIcon = (notification) => {
    const { type } = notification

    switch (type) {
      case 'mention':
        return '@'
      case 'assignment':
        return '📞'
      default:
        return '🔔'
    }
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        className="relative"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className={`
            absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center
            rounded-full bg-red-500 text-xs font-bold text-white
          `}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      <Popover
        placement="bottom end"
        offset={8}
      >
        <Dialog className={`
          max-h-[500px] w-96 overflow-hidden rounded-lg border border-gray-200
          bg-white p-4 shadow-lg outline-none
        `}
        >
          <div className={`
            mb-3 flex items-center justify-between border-b border-gray-100 pb-3
          `}
          >
            <h3 className="text-lg font-semibold">Notifications</h3>
            <div className="flex gap-2">
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteAllNotifications()}
                  title="Delete all notifications"
                >
                  <Trash2 size={14} />
                  Delete all
                </Button>
              )}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markAllAsRead()}
                >
                  <CheckCheck size={14} />
                  Mark all read
                </Button>
              )}
            </div>
          </div>

          <div className="-mx-4 max-h-[400px] overflow-y-auto px-4">
            {isLoading
              ? (
                  <div className="p-8 text-center text-gray-500">
                    Loading notifications...
                  </div>
                )
              : notifications.length === 0
                ? (
                    <div className="p-8 text-center text-gray-500">
                      No notifications yet
                    </div>
                  )
                : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map(notification => (
                        <div
                          key={notification.id}
                          className={cn(
                            `
                              group relative flex w-full items-start p-4
                              text-left transition-colors
                              hover:bg-gray-50
                            `,
                            !notification.is_read && 'bg-blue-50',
                          )}
                        >
                          <button
                            type="button"
                            className="flex-1 text-left"
                            onClick={() => handleNotificationClick(notification)}
                            tabIndex={0}
                          >
                            <div className="flex gap-3">
                              <div className={`
                                flex h-10 w-10 items-center justify-center
                                rounded-full bg-gray-100 text-lg
                              `}
                              >
                                {getNotificationIcon(notification)}
                              </div>
                              <div className="flex-1">
                                <div className={`
                                  flex items-start justify-between
                                `}
                                >
                                  <p className={cn(
                                    'text-sm',
                                    !notification.is_read && 'font-semibold',
                                  )}
                                  >
                                    Someone
                                    {' '}
                                    {getNotificationMessage(notification)}
                                  </p>
                                  {!notification.is_read && (
                                    <span className={`
                                      h-2 w-2 rounded-full bg-blue-500
                                    `}
                                    />
                                  )}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                  {toDtmy(new Date(notification.created_at))}
                                </p>
                              </div>
                            </div>
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`
                              mt-1 ml-2 opacity-60 transition-opacity
                              group-hover:opacity-100
                              hover:opacity-100
                            `}
                            onClick={() => deleteNotification(notification.id)}
                            title="Delete notification"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}
